import React, { useState } from 'react';
import { useProfiles } from '../../hooks/queries/useProfiles';
import { CLOCK_THEMES } from '../../styles/clockThemes';
import styles from './Profile.module.scss';
import { Profile } from '../../types';

interface ThemeColors {
  textColor: string;
  accentColor: string;
  secondaryText: string;
}

interface ChildProfileManagerProps {
  themeColors?: ThemeColors;
}

export const ChildProfileManager: React.FC<ChildProfileManagerProps> = ({ themeColors }) => {
  const { profiles, loading, createProfile, deleteProfile, updateProfile } = useProfiles();
  const [isAdding, setIsAdding] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('kids');

  // Edit State
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editTheme, setEditTheme] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProfileName.trim()) return;

    try {
      await createProfile({ displayName: newProfileName, theme: selectedTheme });
      setNewProfileName('');
      setIsAdding(false);
    } catch (error) {
      console.error('Failed to create profile', error);
    }
  };

  const handleDelete = async (profile: Profile) => {
    const message =
      `Delete "${profile.display_name}"?\n\n` +
      'This permanently deletes this child and ALL of their routines and tasks. ' +
      'This cannot be undone.';
    if (window.confirm(message)) {
      await deleteProfile(profile.id);
    }
  };

  const handleStartEdit = (profile: Profile) => {
    setEditingId(profile.id);
    setEditName(profile.display_name);
    setEditTheme(profile.theme || 'kids');
    setIsAdding(false); // Close add form if open
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditTheme('');
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId || !editName.trim()) return;

    try {
      await updateProfile({ id: editingId, updates: { display_name: editName, theme: editTheme } });
      setEditingId(null);
    } catch (error) {
      console.error('Failed to update profile', error);
    }
  };

  // Theme-aware styles
  const textStyle: React.CSSProperties = themeColors ? { color: themeColors.textColor } : {};
  const secondaryStyle: React.CSSProperties = themeColors ? { color: themeColors.secondaryText } : {};
  const accentBtnStyle: React.CSSProperties = themeColors ? { 
    backgroundColor: themeColors.accentColor,
    color: '#FFFFFF'
  } : {};
  const outlineBtnStyle: React.CSSProperties = themeColors ? { 
    backgroundColor: 'transparent',
    color: themeColors.accentColor,
    border: `1px solid ${themeColors.accentColor}`
  } : {};
  const cardStyle: React.CSSProperties = themeColors ? {
    borderColor: `${themeColors.textColor}20`,
    backgroundColor: `${themeColors.textColor}05`
  } : {};

  // Container card style
  const containerCardStyle: React.CSSProperties = themeColors ? {
    backgroundColor: `${themeColors.textColor}08`,
    border: `1px solid ${themeColors.textColor}15`
  } : {};

  if (loading) return <div style={textStyle}>Loading profiles...</div>;

  return (
    <div className={styles.managerContainer} style={containerCardStyle}>
      <div className={styles.managerHeader}>
        <h3 style={textStyle}>Child Profiles</h3>
        {!editingId && (
          <button 
            onClick={() => setIsAdding(!isAdding)}
            className={styles.addChildBtn}
            style={isAdding ? outlineBtnStyle : accentBtnStyle}
          >
            {isAdding ? 'Cancel' : '+ Add Child'}
          </button>
        )}
      </div>

      {isAdding && (
        <form onSubmit={handleCreate} className={styles.addForm} style={cardStyle}>
          <div className={styles.formGroup}>
            <label style={textStyle}>Name</label>
            <input 
              type="text" 
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
              placeholder="e.g. Max"
              className={styles.textInput}
              style={{ color: themeColors?.textColor, borderColor: `${themeColors?.textColor}30` }}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label style={textStyle}>Theme</label>
            <div className={styles.themeSelection}>
              {Object.values(CLOCK_THEMES).map(theme => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => setSelectedTheme(theme.id)}
                  className={`${styles.themeBtn} ${selectedTheme === theme.id ? styles.active : ''}`}
                  style={selectedTheme === theme.id ? accentBtnStyle : { 
                    color: themeColors?.textColor,
                    borderColor: `${themeColors?.textColor}30`
                  }}
                >
                  {theme.name}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" className={styles.saveBtn} style={accentBtnStyle}>
            Save Profile
          </button>
        </form>
      )}

      <div className={styles.profileList}>
        {profiles.map(profile => {
          if (editingId === profile.id) {
            return (
              <form 
                key={profile.id} 
                onSubmit={handleSaveEdit} 
                className={styles.addForm} 
                style={{ 
                  ...cardStyle,
                  marginBottom: '16px', 
                  border: `2px solid ${themeColors?.accentColor || '#4299E1'}` 
                }}
              >
                <div style={{ 
                  padding: '0 0 16px 0', 
                  borderBottom: `1px solid ${themeColors?.textColor}20`, 
                  marginBottom: '16px', 
                  fontWeight: 600,
                  color: themeColors?.textColor
                }}>
                  Editing Profile
                </div>
                <div className={styles.formGroup}>
                  <label style={textStyle}>Name</label>
                  <input 
                    type="text" 
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className={styles.textInput}
                    style={{ color: themeColors?.textColor, borderColor: `${themeColors?.textColor}30` }}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label style={textStyle}>Theme</label>
                  <div className={styles.themeSelection}>
                    {Object.values(CLOCK_THEMES).map(theme => (
                      <button
                        key={theme.id}
                        type="button"
                        onClick={() => setEditTheme(theme.id)}
                        className={`${styles.themeBtn} ${editTheme === theme.id ? styles.active : ''}`}
                        style={editTheme === theme.id ? accentBtnStyle : { 
                          color: themeColors?.textColor,
                          borderColor: `${themeColors?.textColor}30`
                        }}
                      >
                        {theme.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={styles.buttonRow} style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button type="submit" className={styles.saveBtn} style={{ ...accentBtnStyle, flex: 1 }}>
                    Save Changes
                  </button>
                  <button 
                    type="button" 
                    onClick={handleCancelEdit} 
                    className={styles.deleteBtn} 
                    style={{ 
                      flex: 1, 
                      backgroundColor: `${themeColors?.textColor}10`, 
                      color: themeColors?.textColor 
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            );
          }

          return (
            <div key={profile.id} className={styles.profileItem} style={cardStyle}>
              <div className={styles.profileInfo}>
                <div className={styles.profileName} style={textStyle}>{profile.display_name}</div>
                <div className={styles.profileTheme} style={secondaryStyle}>
                  Theme: {CLOCK_THEMES[profile.theme || 'kids']?.name || profile.theme}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleStartEdit(profile)}
                  className={styles.deleteBtn}
                  style={outlineBtnStyle}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(profile)}
                  className={styles.deleteBtn}
                  style={{ backgroundColor: '#D65A4A', color: '#FFFFFF' }}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
        {profiles.length === 0 && !isAdding && (
          <div className={styles.emptyState} style={secondaryStyle}>
            No child profiles yet. Create one to get started!
          </div>
        )}
      </div>
    </div>
  );
};
